import ApiERP from '@/core/conectores/ApiERP';
import { IFiltroGenerico } from '@/core/models/BuscaAvancada/IFiltroGenerico';
import { IItemConsultaRapida } from '@/core/models/Consulta/IItemConsultaRapida';
import { IListaPaginada } from '@/core/models/Consulta/IListaPaginada';
import { IParametrosConsulta } from '@/core/models/Consulta/IParametrosConsulta';
import { IParametrosConsultaRapida } from '@/core/models/Consulta/IParametrosConsultaRapida';
import { IPropriedadeConsulta } from '@/core/models/Consulta/PropriedadeConsulta';
import { ETipoArquivo } from '@/core/models/Enumeradores/ETipoArquivo';
import { IRetornoRelatorio } from '@/core/models/IRetornoRelatorio';
import { IRetornoRequisicao } from '@/core/models/IRetornoRequisicao';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import { IOrdenacaoRegistro } from '@/core/models/IOrdenacaoRegistro';
import storeSistema from '@/store/storeSistema';
import { IParametrosConsultaRecebimentoMercadoria } from '@/models/ParametrosRequisicao/Compras/IParametrosConsultaRecebimentoMercadoria';
import { INotaFiscalIncidenciaCalculo } from '@/models/Entidades/Fiscal/INotaFiscal';
import { IDTORecebimentoMercadoriasBuscaFinanceiro } from '@/models/DTO/Compras/IDTORecebimentoMercadoriasBuscaFinanceiro';
import { IDTOTituloFinanceiro } from '@/models/DTO/Financeiro/TitulosFinanceiros/IDTOTituloFinanceiro';
import { IPedidoCompra, IPedidoCompraDetalhePagamento, IPedidoCompraItem } from '@/models/Entidades/Compras/IPedidoCompra';
import { EStatusPedidoCompra } from '@/models/Enumeradores/Compras/EStatusPedidoCompra';
import { IRecebimentoMercadoria } from '@/models/Entidades/Compras/IRecebimentoMercadoria';
import { EFiltroPeriodoMovimentoComercial } from '@/models/Enumeradores/Compartilhados/EFiltroPeriodoMovimentoComercial';
import { IDTOPedidoCompra } from '@/models/DTO/Compras/IDTOPedidoCompra';

/**
 * Serviço de Recebimentos de Mercadorias
 * Fornece todas regras de negócios e lógicas relacionado a recebimentos de mercadorias.
 */

class ServicoPedidoCompra {
    endPoint = 'pedido-compras';

    apiERP = new ApiERP();

    public requisicaoSistema():void {
      this.apiERP = new ApiERP(true);
    }

    public async obterPropriedadesConsulta(): Promise<IPropriedadeConsulta[]> {
      const listaPropriedades = await this.apiERP.obterPropriedadesConsulta(this.endPoint);
      return listaPropriedades;
    }

    public async buscaAvancada(parametrosConsulta: IParametrosConsultaRecebimentoMercadoria, filtros?: IFiltroGenerico[]): Promise<IListaPaginada> {
      let parametrosAdicionais = '';
      if (parametrosConsulta.empresas !== undefined && parametrosConsulta.empresas.length > 0) {
        parametrosConsulta.empresas.forEach((codigoEmpresa) => {
          parametrosAdicionais += (parametrosAdicionais !== '' ? '&' : '?');
          parametrosAdicionais += `Empresas=${codigoEmpresa}`;
        });
      }

      if (parametrosConsulta.periodo !== undefined) {
        parametrosAdicionais += (parametrosAdicionais !== '' ? '&' : '?');
        parametrosAdicionais += `periodo=${parametrosConsulta.periodo}`;

        parametrosAdicionais += `&dataInicial=${parametrosConsulta.dataInicial}`;
        parametrosAdicionais += `&dataFinal=${parametrosConsulta.dataFinal}`;
      }

      if (parametrosConsulta.valorBuscaRapida !== undefined) {
        if (UtilitarioGeral.valorValido(parametrosConsulta.valorBuscaRapida)) {
          parametrosAdicionais += (parametrosAdicionais !== '' ? '&' : '?');
          parametrosAdicionais += `valorBuscaRapida=${parametrosConsulta.valorBuscaRapida}`;
        }
      }

      if (filtros !== undefined) {
        const jsonFiltros = JSON.stringify(filtros);
        parametrosAdicionais += `&Filtros=${jsonFiltros}`;
      }
      if (parametrosConsulta.numeroPagina !== undefined && parametrosConsulta.numeroPagina > 0) {
        parametrosAdicionais += `&NumeroPagina=${parametrosConsulta.numeroPagina}`;
      }
      if (parametrosConsulta.qtdeRegistrosPagina !== undefined && parametrosConsulta.qtdeRegistrosPagina > 0) {
        parametrosAdicionais += `&QtdeRegistrosPagina=${parametrosConsulta.qtdeRegistrosPagina}`;
      }
      if (parametrosConsulta.qtdeRegistrosTotal !== undefined && parametrosConsulta.qtdeRegistrosTotal > 0) {
        parametrosAdicionais += `&QtdeRegistrosTotal=${parametrosConsulta.qtdeRegistrosTotal}`;
      }
      if (parametrosConsulta.ordenacao !== undefined) {
        parametrosConsulta.ordenacao.forEach((item) => {
          parametrosAdicionais += `&Ordenacao=${item}`;
        });
      }
      if (parametrosConsulta.codigosSelecionados !== undefined) {
        parametrosConsulta.codigosSelecionados.forEach((codigo) => {
          parametrosAdicionais += `&CodigosSelecionados=${codigo}`;
        });
      }

      const result: any = await this.apiERP.get(`${this.endPoint}${parametrosAdicionais}`);
      const listaPaginada = {} as IListaPaginada;
      listaPaginada.metaData = JSON.parse(result.headers['x-paginacao']);
      listaPaginada.dados = result.data;
      return listaPaginada;
    }

    public async buscaAvancadaItens(codigoFornecedor: number, parametrosConsulta: IParametrosConsulta, filtros?: IFiltroGenerico[]): Promise<IListaPaginada> {
      const parametrosEmpresas = this.apiERP.obterParametroEmpresas(parametrosConsulta.empresas);
      let parametrosAdicionais = this.apiERP.obterParametrosAdicionaisConsulta(parametrosConsulta, filtros);
      if (UtilitarioGeral.validaCodigo(codigoFornecedor)) {
        parametrosAdicionais += `&codigoFornecedor=${codigoFornecedor}`;
      }
      const result: any = await this.apiERP.get(`${this.endPoint}/itens/${parametrosEmpresas}${parametrosAdicionais}`);
      const listaPaginada = {} as IListaPaginada;
      listaPaginada.metaData = JSON.parse(result.headers['x-paginacao']);
      listaPaginada.dados = result.data;
      return listaPaginada;
    }

    public async relatorioPadrao(tipoArquivo: ETipoArquivo, parametrosConsulta: IParametrosConsultaRecebimentoMercadoria, filtros?: IFiltroGenerico[], codigosSelecionados?: Array<number>): Promise<IRetornoRelatorio> {
      const parametrosEmpresas = this.apiERP.obterParametroEmpresas(parametrosConsulta.empresas);
      let parametrosAdicionais = this.apiERP.obterParametrosAdicionaisConsulta(parametrosConsulta, filtros);
      if (parametrosConsulta.periodo !== undefined) {
        parametrosAdicionais += (parametrosAdicionais !== '' ? '&' : '?');
        parametrosAdicionais += `periodo=${parametrosConsulta.periodo}`;
        parametrosAdicionais += `&dataInicial=${parametrosConsulta.dataInicial}`;
        parametrosAdicionais += `&dataFinal=${parametrosConsulta.dataFinal}`;
      }
      if (codigosSelecionados !== undefined) {
        parametrosAdicionais += this.apiERP.obterParametroCodigosSelecionados(codigosSelecionados);
      }
      const result: any = await this.apiERP.get(`${this.endPoint}/relatorio-padrao/${tipoArquivo}${parametrosEmpresas}${parametrosAdicionais}`);
      return result.data;
    }

    public obterIdentificacaoRegistroGradeCRUD(objeto: IPedidoCompra): string {
      return String(objeto.codigo);
    }

    public async consultaRapida(parametrosConsultaRapida: IParametrosConsultaRapida): Promise<IItemConsultaRapida[]> {
      const parametrosAdicionais = this.apiERP.obterParametrosAdicionaisConsultaRapida(parametrosConsultaRapida);
      const result: any = await this.apiERP.get(`${this.endPoint}/consulta-rapida${parametrosAdicionais}`);
      return result.data;
    }

    public async obterPedidoCompraConsultaRapida(codigoRecebimento: number, empresas?: number[]): Promise<IItemConsultaRapida> {
      const parametrosConsultaRapida: IParametrosConsultaRapida = {
        valor: codigoRecebimento.toString(), filtrarPorCodigo: true, recursoAssociado: '',
      };

      if (UtilitarioGeral.validaLista(empresas)) {
        parametrosConsultaRapida.empresas = empresas;
      } else {
        parametrosConsultaRapida.empresas = storeSistema.getters.empresasUsuarioAutenticado();
      }

      const listaPedidos = await this.consultaRapida(parametrosConsultaRapida);
      if (UtilitarioGeral.validaLista(listaPedidos)) {
        return listaPedidos[0];
      }

      return {} as IItemConsultaRapida;
    }

    public async incluir(objeto: IPedidoCompra): Promise<IRetornoRequisicao> {
      const result: any = await this.apiERP.post(`${this.endPoint}`, objeto);
      return result.data;
    }

    public async incluirCabecalho(objeto: IPedidoCompra): Promise<IRetornoRequisicao> {
      const result: any = await this.apiERP.post(`${this.endPoint}/cabecalho`, objeto);
      return result.data;
    }

    public async alterar(objeto: IPedidoCompra): Promise<IRetornoRequisicao> {
      const result: any = await this.apiERP.put(`${this.endPoint}/${objeto.codigo}`, objeto);
      return result.data;
    }

    public async atualizarCampoCabecalho(objeto: IPedidoCompra, campo: string): Promise<IRetornoRequisicao> {
      const result: any = await this.apiERP.put(`${this.endPoint}/campo/${campo}`, objeto);
      return result.data;
    }

    public async atualizarItem(item: IPedidoCompraItem, valorTotal: number, empresa: number): Promise<IRetornoRequisicao> {
      const result: any = await this.apiERP.put(`${this.endPoint}/item/valor-total/${valorTotal}/empresa/${empresa}`, item);
      return result.data;
    }

    public async atualizarOrdenacao(ordenacaoRegistros: IOrdenacaoRegistro[], empresa: number): Promise<IRetornoRequisicao> {
      const result: any = await this.apiERP.put(`${this.endPoint}/ordenacao/empresa/${empresa}`, ordenacaoRegistros);
      return result.data;
    }

    public async alterarEtapa(codigo: number, empresa: number, statusNovo: EStatusPedidoCompra): Promise<IRetornoRequisicao> {
      const result: any = await this.apiERP.put(`${this.endPoint}/etapa/${codigo}/empresa/${empresa}/status-novo/${statusNovo}`);
      return result.data;
    }

    public async alterarEtapaVarios(empresa: number, proximaEtapa: boolean, cancelamento: boolean, recebimentos: number[]): Promise<IRetornoRequisicao> {
      let parametrosAdicionais = '';
      for (let contador = 0; contador < recebimentos.length; contador += 1) {
        parametrosAdicionais += (parametrosAdicionais !== '' ? '&' : '?');
        parametrosAdicionais += `Recebimentos=${recebimentos[contador]}`;
      }

      const result: any = await this.apiERP.put(`${this.endPoint}/etapa-varios/empresa/${empresa}/proxima-etapa/${proximaEtapa}/cancelamento/${cancelamento}${parametrosAdicionais}`);
      return result.data;
    }

    public async excluir(codigo: number, empresa: number): Promise<IRetornoRequisicao> {
      const result: any = await this.apiERP.delete(`${this.endPoint}/${codigo}/empresa/${empresa}`);
      return result.data;
    }

    public async excluirVarios(empresa: number, recebimentos: number[]): Promise<IRetornoRequisicao> {
      let parametrosAdicionais = '';
      for (let contador = 0; contador < recebimentos.length; contador += 1) {
        parametrosAdicionais += (parametrosAdicionais !== '' ? '&' : '?');
        parametrosAdicionais += `Recebimentos=${recebimentos[contador]}`;
      }

      const result: any = await this.apiERP.delete(`${this.endPoint}/varios/empresa/${empresa}${parametrosAdicionais}`);
      return result.data;
    }

    public async excluirItem(codigo: number, empresa: number, valorTotal: number): Promise<IRetornoRequisicao> {
      const result: any = await this.apiERP.delete(`${this.endPoint}/item/${codigo}/empresa/${empresa}/valor-total/${valorTotal}`);
      return result.data;
    }

    public async obter(codigo: number, empresa: number): Promise<IPedidoCompra> {
      const result: any = await this.apiERP.get(`${this.endPoint}/${codigo}/empresa/${empresa}`);
      return result.data;
    }

    public async obterPorFornecedor(fornecedor: number, empresa: number): Promise<IListaPaginada> {
      const result: any = await this.apiERP.get(`${this.endPoint}/${fornecedor}/fornecedor/${empresa}`);
      const listaPaginada = {} as IListaPaginada;
      listaPaginada.dados = result.data;
      return listaPaginada;
    }

    public async obterPorNumero(codigo: number, empresa: number): Promise<IPedidoCompra> {
      const result: any = await this.apiERP.get(`${this.endPoint}/numero/${codigo}/empresa/${empresa}`);
      return result.data;
    }

    public async cancelar(codigo: number, empresa: number): Promise<IRetornoRequisicao> {
      const result: any = await this.apiERP.put(`${this.endPoint}/cancelar/${codigo}/empresa/${empresa}`);
      return result.data;
    }

    public async obterItem(codigoItem: number, empresa: number): Promise<IPedidoCompraItem> {
      const result: any = await this.apiERP.get(`${this.endPoint}/item/${codigoItem}/empresa/${empresa}`);
      return result.data;
    }

    public async obterCodigoItem(codigo: number, empresa: number, codigoProduto: number): Promise<number> {
      const result: any = await this.apiERP.get(`${this.endPoint}/${codigo}/empresa/${empresa}/produto/${codigoProduto}`);
      return result.data;
    }

    public async obterItens(pedido: number, notaFiscal: number, empresa: number): Promise<IPedidoCompraItem[]> {
      const result: any = await this.apiERP.get(`${this.endPoint}/itens/${pedido}/nota-fiscal/${notaFiscal}/empresa/${empresa}`);
      return result.data;
    }

    public async obterItensPedidosCompletos(pedido: number, empresa: number): Promise<IPedidoCompraItem[]> {
      const result: any = await this.apiERP.get(`${this.endPoint}/itens-completos/${pedido}/empresa/${empresa}`);
      return result.data;
    }

    public async obterDetalhePagamento(pedido: number): Promise<IPedidoCompraDetalhePagamento[]> {
      const result: any = await this.apiERP.get(`${this.endPoint}/${pedido}/detalhe-pagamento/`);
      return result.data;
    }

    public async obterItensCompletos(empresa: number, codigos: number[]): Promise<IPedidoCompraItem[]> {
      let parametrosAdicionais = '';
      for (let contador = 0; contador < codigos.length; contador += 1) {
        parametrosAdicionais += (parametrosAdicionais !== '' ? '&' : '?');
        parametrosAdicionais += `Codigos=${codigos[contador]}`;
      }

      const result: any = await this.apiERP.get(`${this.endPoint}/itens/empresa/${empresa}${parametrosAdicionais}`);
      return result.data;
    }

    public async obterTitulosFinanceiros(buscaFinanceiro: IDTORecebimentoMercadoriasBuscaFinanceiro): Promise<IDTOTituloFinanceiro[]> {
      const result: any = await this.apiERP.post(`${this.endPoint}/titulos-financeiros/busca`, buscaFinanceiro);
      return result.data;
    }

    public async ImprimirPedidoCompra(codigo: number, empresa: number, tipoArquivo: ETipoArquivo): Promise<IRetornoRelatorio> {
      const result: any = await this.apiERP.get(`${this.endPoint}/impressao/${codigo}/empresa/${empresa}/tipo-arquivo/${tipoArquivo}`);
      return result.data;
    }

    public async importarPedidoCompra(codigoPedido: number, empresa: number): Promise<IRecebimentoMercadoria> {
      const result: any = await this.apiERP.post(`${this.endPoint}/pedido-compra/${codigoPedido}/empresa/${empresa}/importar`);
      return result.data;
    }

    public async obterPedidoCompra(empresa: number, valorBusca: string, periodo:EFiltroPeriodoMovimentoComercial, status: EStatusPedidoCompra, dataInicial?: string, dataFinal?: string): Promise<IDTOPedidoCompra[]> {
      let parametrosAdicionais = '';

      if (UtilitarioGeral.valorValido(valorBusca)) {
        parametrosAdicionais += (parametrosAdicionais !== '' ? '&' : '?');
        parametrosAdicionais += `valorBusca=${valorBusca}`;
      }

      if (UtilitarioGeral.valorValido(periodo)) {
        parametrosAdicionais += (parametrosAdicionais !== '' ? '&' : '?');
        parametrosAdicionais += `periodo=${periodo}`;
      }

      if (UtilitarioGeral.valorValido(status)) {
        parametrosAdicionais += (parametrosAdicionais !== '' ? '&' : '?');
        parametrosAdicionais += `status=${status}`;
      }

      if (UtilitarioGeral.valorValido(dataInicial)) {
        parametrosAdicionais += (parametrosAdicionais !== '' ? '&' : '?');
        parametrosAdicionais += `dataInicial=${dataInicial}`;
      }

      if (UtilitarioGeral.valorValido(dataFinal)) {
        parametrosAdicionais += (parametrosAdicionais !== '' ? '&' : '?');
        parametrosAdicionais += `dataFinal=${dataFinal}`;
      }

      const result: any = await this.apiERP.get(`${this.endPoint}/pedido-compra/empresa/${empresa}${parametrosAdicionais}`);
      return result.data;
    }

    public async obterPedidosCompraRecebimento(empresa: number, codigosPedidosCompra: number[]): Promise<IDTOPedidoCompra[]> {
      let parametrosAdicionais = '';
      for (let contador = 0; contador < codigosPedidosCompra.length; contador += 1) {
        parametrosAdicionais += (parametrosAdicionais !== '' ? '&' : '?');
        parametrosAdicionais += `codigos=${codigosPedidosCompra[contador]}`;
      }
      console.log('Parametros Adicionais: ', parametrosAdicionais);
      const result: any = await this.apiERP.get(`${this.endPoint}/recebimento/empresa/${empresa}${parametrosAdicionais}`);
      return result.data;
    }

    public obterIncidenciasNotaFiscalRecebimentoPadrao(): INotaFiscalIncidenciaCalculo[] {
      const incidenciasCalculo:INotaFiscalIncidenciaCalculo[] = [
        {
          codigo: 0, codigoNotaFiscal: 0, incidencia: 'ICMS', identificador: 'Frete', valor: true,
        },
        {
          codigo: 0, codigoNotaFiscal: 0, incidencia: 'ICMS', identificador: 'OutrasDespesas', valor: true,
        },
        {
          codigo: 0, codigoNotaFiscal: 0, incidencia: 'ICMS', identificador: 'Seguro', valor: false,
        },
        {
          codigo: 0, codigoNotaFiscal: 0, incidencia: 'ICMS', identificador: 'IPI', valor: false,
        },
        {
          codigo: 0, codigoNotaFiscal: 0, incidencia: 'ICMS', identificador: 'Desconto', valor: true,
        },
        {
          codigo: 0, codigoNotaFiscal: 0, incidencia: 'IPI', identificador: 'Frete', valor: true,
        },
        {
          codigo: 0, codigoNotaFiscal: 0, incidencia: 'IPI', identificador: 'OutrasDespesas', valor: true,
        },
        {
          codigo: 0, codigoNotaFiscal: 0, incidencia: 'IPI', identificador: 'Seguro', valor: false,
        },
        {
          codigo: 0, codigoNotaFiscal: 0, incidencia: 'IPI', identificador: 'Desconto', valor: true,
        },
        {
          codigo: 0, codigoNotaFiscal: 0, incidencia: 'PIS', identificador: 'Frete', valor: true,
        },
        {
          codigo: 0, codigoNotaFiscal: 0, incidencia: 'PIS', identificador: 'OutrasDespesas', valor: true,
        },
        {
          codigo: 0, codigoNotaFiscal: 0, incidencia: 'PIS', identificador: 'Seguro', valor: false,
        },
        {
          codigo: 0, codigoNotaFiscal: 0, incidencia: 'PIS', identificador: 'Desconto', valor: true,
        },
        {
          codigo: 0, codigoNotaFiscal: 0, incidencia: 'COFINS', identificador: 'Frete', valor: true,
        },
        {
          codigo: 0, codigoNotaFiscal: 0, incidencia: 'COFINS', identificador: 'OutrasDespesas', valor: true,
        },
        {
          codigo: 0, codigoNotaFiscal: 0, incidencia: 'COFINS', identificador: 'Seguro', valor: false,
        },
        {
          codigo: 0, codigoNotaFiscal: 0, incidencia: 'COFINS', identificador: 'Desconto', valor: true,
        },
        {
          codigo: 0, codigoNotaFiscal: 0, incidencia: 'TotalNota', identificador: 'Frete', valor: true,
        },
        {
          codigo: 0, codigoNotaFiscal: 0, incidencia: 'TotalNota', identificador: 'OutrasDespesas', valor: true,
        },
        {
          codigo: 0, codigoNotaFiscal: 0, incidencia: 'TotalNota', identificador: 'Seguro', valor: true,
        },
        {
          codigo: 0, codigoNotaFiscal: 0, incidencia: 'TotalNota', identificador: 'IPI', valor: true,
        },
        {
          codigo: 0, codigoNotaFiscal: 0, incidencia: 'TotalNota', identificador: 'Desconto', valor: true,
        },
      ];

      return incidenciasCalculo;
    }
}
export default ServicoPedidoCompra;
